import type {
    GoogleMapBounds,
    GoogleMapInstance,
    GoogleMapLatLng,
    GoogleMapLatLngLiteral,
} from '@/features/map/google-map/google-map-types';
import type { IMapLatLngLiteral, IMapPin, IMapPinsRecord } from '@/features/map/map-data-v5/map-types';

export type BoundariesType = {
    fromLatitude: string;
    fromLongitude: string;
    toLatitude: string;
    toLongitude: string;
    zoom: string;
};

export type FlatMultiPinsRecord = Record<string, IMapPin & { multiPinKey: string }>;

export const getBoundariesObject = (boundaries: GoogleMapBounds, zoom: number): BoundariesType => {
    return {
        fromLatitude: boundaries.getSouthWest().lat().toString(),
        fromLongitude: boundaries.getSouthWest().lng().toString(),
        toLatitude: boundaries.getNorthEast().lat().toString(),
        toLongitude: boundaries.getNorthEast().lng().toString(),
        zoom: zoom.toString(),
    };
};

export const getMapLatLngLiteral = (center?: GoogleMapLatLng): IMapLatLngLiteral => {
    return {
        latitude: parseFloat((center?.lat() ?? 0).toFixed(10)),
        longitude: parseFloat((center?.lng() ?? 0).toFixed(10)),
    };
};

export const getMultiRecordFromPins = (pins: IMapPinsRecord): FlatMultiPinsRecord => {
    return Object.entries(pins).reduce((previousPins, [nextPinKey, nextPinValue]) => {
        if (nextPinValue.markerType !== 'multi') {
            return previousPins;
        }
        const multiPins = Object.entries(nextPinValue.pins).reduce(
            (previousMultiPins, [nextMultiPinKey, nextMultiPinValue]) => ({
                ...previousMultiPins,
                [nextMultiPinKey]: { ...nextMultiPinValue, multiPinKey: nextPinKey },
            }),
            {},
        );
        return { ...previousPins, ...multiPins };
    }, {});
};

/**
 * Returns a boundary with adjusted latitude values to contain all pins. Longitude values are the same as in the current view
 * @param googleMapInstance
 * @param pins
 * @param reference contains the base boundary and zoom level on to filter out of boundary pins and calculate padding
 * @param config default 20px padding
 */
export const getAdjustedPinBoundary = (
    googleMapInstance: GoogleMapInstance,
    reference: { boundary: GoogleMapBounds; zoom: number },
    pins: IMapPinsRecord,
    config?: {
        padding?: {
            paddingHorizontal?: number;
            paddingVertical?: number;
        };
    },
) => {
    const boundary = new google.maps.LatLngBounds();
    const { paddingHorizontal = 20, paddingVertical = 20 } = config?.padding ?? {};

    // The Api might return Pins that are not in the viewport because they belong to a large hiking route
    // Spot (e.g., Jakobsweg). If we included those pins, it would have skewed the target boundary
    const filteredPins = Object.values(pins).filter((pin) => {
        return 'point' in pin && reference.boundary.contains({ lat: pin.point.latitude, lng: pin.point.longitude });
    });

    if (filteredPins.length === 0) {
        return null;
    }

    filteredPins.forEach((pin) => {
        if ('point' in pin) {
            const latLng = new google.maps.LatLng(pin.point.latitude, pin.point.longitude);
            boundary.extend(latLng);
        }
    });

    // Add padding to the boundary
    const paddedNorthEastPosition = shiftMapPositionByPixel(
        googleMapInstance,
        {
            location: { lat: boundary.getNorthEast().lat(), lng: boundary.getNorthEast().lng() },
            zoom: reference.zoom,
        },
        {
            x: paddingHorizontal,
            y: -paddingVertical,
        },
    );
    const paddedSouthWestPosition = shiftMapPositionByPixel(
        googleMapInstance,
        {
            location: { lat: boundary.getSouthWest().lat(), lng: boundary.getSouthWest().lng() },
            zoom: reference.zoom,
        },
        {
            x: -paddingHorizontal,
            y: paddingVertical,
        },
    );

    // Add two points to the boundary. They are used to set the minimum width of the target boundary to
    // match the original boundary to avoid large zoom ins'
    const topRightPoint = {
        lat: boundary.getNorthEast().lat(),
        lng: reference.boundary.getNorthEast().lng(),
    };

    const bottomLeftPoint = {
        lat: boundary.getSouthWest().lat(),
        lng: reference.boundary.getSouthWest().lng(),
    };

    if (!paddedNorthEastPosition || !paddedSouthWestPosition) {
        return null;
    }

    boundary.extend(paddedNorthEastPosition.location);
    boundary.extend(paddedSouthWestPosition.location);
    boundary.extend(topRightPoint);
    boundary.extend(bottomLeftPoint);

    return boundary;
};

/**
 * Takes a boundary and calculates the position and zoom level to fit the boundary within half the map viewport.
 * @param mapSize
 * @param boundary
 */
export const getHalfViewportPositionFromBoundary = (
    mapSize: { height: number; width: number },
    boundary: GoogleMapBounds | null,
) => {
    if (boundary === null) {
        return null;
    }

    const center = boundary.getCenter();
    const { height: mapHeight, width: mapWidth } = mapSize;
    const MAP_TILE_SIZE = { height: 256, width: 256 };
    const MAX_ZOOM = 21;

    const northEastPoint = {
        lat: boundary.getNorthEast().lat(),
        lng: boundary.getNorthEast().lng(),
    };

    const southWestPoint = {
        lat: boundary.getSouthWest().lat(),
        lng: boundary.getSouthWest().lng(),
    };

    // Special case for latitude takes into account mercator projection
    const latRad = (lat: number) => {
        const sin = Math.sin((lat * Math.PI) / 180);
        const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
        return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    };

    const latFraction = (latRad(northEastPoint.lat) - latRad(southWestPoint.lat)) / Math.PI;

    const lngDiff = northEastPoint.lng - southWestPoint.lng;
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

    const latZoom = Math.log(mapHeight / 2 / MAP_TILE_SIZE.height / latFraction) / Math.LN2;
    const lngZoom = Math.log(mapWidth / MAP_TILE_SIZE.width / lngFraction) / Math.LN2;

    const zoom = Math.min(latZoom, lngZoom, MAX_ZOOM);

    return {
        boundary,
        location: {
            lat: center.lat(),
            lng: center.lng(),
        },
        zoom,
    };
};

export const convertPositionToBoundary = (
    googleMapProjection: google.maps.Projection,
    mapSize: { height: number; width: number },
    position: { location: GoogleMapLatLngLiteral; zoom: number },
): GoogleMapBounds | null => {
    const { location, zoom } = position;
    const { height, width } = mapSize;

    const scale = Math.pow(2, zoom);
    const postionPoint = googleMapProjection.fromLatLngToPoint(location); // get the location in Pixel Space

    if (!postionPoint) {
        return null;
    }

    // calculate the pixel offset based on the desired zoom level/scale // in Pixel Space
    const pixelOffsetX = width / 2 / scale;
    const pixelOffsetY = height / 2 / scale;

    // based on the map height and width, calculate the points that define the boundaries // in Pixel Space
    const southWestPoint = new google.maps.Point(postionPoint.x - pixelOffsetX, postionPoint.y + pixelOffsetY);
    const northEastPoint = new google.maps.Point(postionPoint.x + pixelOffsetX, postionPoint.y - pixelOffsetY);

    // convert points in pixel space to points in coordinate space
    const southWestLatLng = googleMapProjection.fromPointToLatLng(southWestPoint);
    const northEastLatLng = googleMapProjection.fromPointToLatLng(northEastPoint);

    return new google.maps.LatLngBounds(southWestLatLng, northEastLatLng);
};

export const shiftMapPositionByPixel = (
    googleMapInstance: GoogleMapInstance,
    position: { location: GoogleMapLatLngLiteral; zoom: number },
    pixelOffset: { x?: number; y?: number },
): { location: GoogleMapLatLngLiteral; zoom: number } | null => {
    const { x = 0, y = 0 } = pixelOffset;
    const projection = googleMapInstance.getProjection();

    if (!projection) {
        return null;
    }

    const { location, zoom } = position;

    const scale = Math.pow(2, zoom);
    const positionPoint = projection.fromLatLngToPoint(location); // get the location in Pixel-Space

    if (!positionPoint) {
        return null;
    }

    // Apply the pixel offset for the specified zoom level
    const shiftedPoint = new google.maps.Point(positionPoint.x + x / scale, positionPoint.y + y / scale);

    // Convert from Pixel-Space to Coordinate-Space
    const shiftedLatLng = projection.fromPointToLatLng(shiftedPoint);

    if (!shiftedLatLng) {
        throw new Error('Could not determine shifted latitude/longitude');
    }

    return {
        location: {
            lat: shiftedLatLng.lat(),
            lng: shiftedLatLng.lng(),
        },
        zoom: position.zoom,
    };
};
